import React, { useRef, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Fab,
  alpha,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { lightGreen, orange } from '@mui/material/colors';
import { useWarbandStore } from '../../stores/warbandStore';
import { useAutoFlush } from '../../hooks/useAutoFlush';
import { ManageWarbandPage } from './ManageWarbandPage';
import { PreGamePage } from './PreGamePage';
import { PlayWarbandPage } from './PlayWarbandPage';
import { PostGamePage } from './PostGamePage';
import { SaveStatusIndicator } from '../shared/SaveStatusIndicator';
import { WarbandPage } from '../../types';

const pageOrder: WarbandPage[] = ['manage', 'pregame', 'play', 'postgame'];

const pageComponents: Record<WarbandPage, React.FC> = {
  manage: ManageWarbandPage,
  pregame: PreGamePage,
  play: PlayWarbandPage,
  postgame: PostGamePage,
};

export const WarbandLayout: React.FC = () => {
  const {
    currentPage,
    lastNonManagePage,
    selectedWarbandId,
    isLoading,
    error,
    navigateToPage,
    isMinified,
    setIsMinified,
  } = useWarbandStore();

  // Switch body background based on mode
  useEffect(() => {
    const body = document.body;
    if (currentPage === 'manage') {
      body.style.backgroundImage = 'url(/camp.jpeg)';
      body.style.backgroundSize = 'cover';
      body.style.backgroundRepeat = 'no-repeat';
      body.style.backgroundPosition = 'center';
    } else {
      body.style.backgroundImage = 'url(/city.jpeg)';
      body.style.backgroundSize = 'cover';
      body.style.backgroundRepeat = 'no-repeat';
      body.style.backgroundPosition = 'center';
    }
    return () => {
      body.style.backgroundImage = 'url(/city.jpeg)';
      body.style.backgroundSize = 'cover';
      body.style.backgroundRepeat = 'no-repeat';
      body.style.backgroundPosition = 'center';
    };
  }, [currentPage]);
  // Track which pages have been visited for lazy-mount keep-alive.
  // Mutating the ref during render ensures the current page is always
  // included before the JSX map runs — no effect delay, no flash.
  const mountedPagesRef = useRef<Set<WarbandPage>>(new Set());
  mountedPagesRef.current.add(currentPage);

  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-flush dirty changes periodically, on tab blur, and on page unload
  useAutoFlush(selectedWarbandId);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          pr: 1,
          pl: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ position: 'fixed', top: 72, right: 16, zIndex: 1100 }}>
          <SaveStatusIndicator />
        </Box>

        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Keep all visited pages mounted; hide inactive ones via CSS so
            component trees and expensive ice surface hooks survive navigation. */}
        <Box sx={{ display: isLoading ? 'none' : 'flex', flex: 1, minHeight: 0, flexDirection: 'column' }}>
          {pageOrder.map((page) => {
            if (!mountedPagesRef.current.has(page)) return null;
            const PageComponent = pageComponents[page];
            return (
              <Box key={page} sx={{ display: page === currentPage ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
                <PageComponent />
              </Box>
            );
          })}
        </Box>

        {/* Floating Action Button: Zoom In / Out */}
        <Fab
          color="primary"
          size="small"
          onClick={() => setIsMinified(!isMinified)}
          sx={{ position: 'fixed', top: 85, right: 16, zIndex: 50 }}
        >
          {isMinified ? <ZoomInIcon /> : <ZoomOutIcon />}
        </Fab>

        {/* Floating Action Button to toggle between manage and pregame */}
        {selectedWarbandId && (
          <Fab
            color='primary'
            aria-label="toggle-page"
            sx={{
              position: 'fixed',
              bottom: 56,
              left: 16,
              zIndex: 1200,
              backgroundColor: currentPage === 'manage' ? alpha(lightGreen['A400'], 0.52) : alpha(orange['A400'], 0.52),
              ":hover": {
                backgroundColor: currentPage === 'manage' ? alpha(lightGreen['A400'], 0.65) : alpha(orange['A400'], 0.65),
              },
            }}
            onClick={() => {
              if (currentPage === 'manage') {
                navigateToPage(lastNonManagePage || 'pregame');
              } else {
                navigateToPage('manage');
              }
            }}
          >
            {currentPage === 'manage' ? <PlayArrowIcon /> : <SettingsIcon />}
          </Fab>
        )}
      </Box>
    </Box>
  );
};
